/**
 * Ringtone playback. The tone is generated in-memory so no binary asset is added to the repo.
 */
const SAMPLE_RATE = 8000
const TONE_HZ = 480
const TONE_SECONDS = 1

let audioElement = null
let isArmed = false

function buildToneDataUri() {
  const sampleCount = SAMPLE_RATE * TONE_SECONDS
  const bytes = new Uint8Array(44 + sampleCount)
  const view = new DataView(bytes.buffer)

  const writeAscii = (offset, text) => {
    for (let i = 0; i < text.length; i++) {
      view.setUint8(offset + i, text.charCodeAt(i))
    }
  }

  writeAscii(0, 'RIFF')
  view.setUint32(4, 36 + sampleCount, true)
  writeAscii(8, 'WAVEfmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, SAMPLE_RATE, true)
  view.setUint16(32, 1, true)
  view.setUint16(34, 8, true)
  writeAscii(36, 'data')
  view.setUint32(40, sampleCount, true)

  for (let i = 0; i < sampleCount; i++) {
    // Half a second of tone then half a second of silence, so looping sounds like a ring.
    const isSilent = i > sampleCount / 2
    const sample = isSilent ? 0 : Math.sin((2 * Math.PI * TONE_HZ * i) / SAMPLE_RATE) * 0.3

    view.setUint8(44 + i, 128 + Math.round(sample * 127))
  }

  let binary = ''
  bytes.forEach(byte => { binary += String.fromCharCode(byte) })

  return `data:audio/wav;base64,${btoa(binary)}`
}

function getAudioElement() {
  if (typeof Audio === 'undefined') {
    return null
  }

  if (!audioElement) {
    audioElement = new Audio(buildToneDataUri())
    audioElement.loop = true
  }

  return audioElement
}

/**
 * Browsers refuse to play audio until the user has interacted with the page, and an inbound
 * call arrives with no gesture of its own. Going online IS a click, so unlock the element
 * there by starting and immediately stopping it; later `startRinging` calls are then allowed.
 */
export function armRingtone() {
  const audio = getAudioElement()

  if (!audio) {
    return Promise.resolve(false)
  }

  audio.muted = true

  return Promise.resolve(audio.play())
    .then(() => {
      audio.pause()
      audio.currentTime = 0
      audio.muted = false
      isArmed = true

      return true
    })
    .catch(() => {
      audio.muted = false

      return false
    })
}

export function isRingtoneArmed() {
  return isArmed
}

export function startRinging() {
  const audio = getAudioElement()

  if (!audio) {
    return
  }

  audio.currentTime = 0

  Promise.resolve(audio.play()).catch(() => {
    // Autoplay still blocked (agent never clicked). The visual ringing state is the fallback.
  })
}

export function stopRinging() {
  if (!audioElement) {
    return
  }

  audioElement.pause()
  audioElement.currentTime = 0
}

export function disposeRingtone() {
  stopRinging()

  audioElement = null
  isArmed = false
}
